import type { ReactNode } from 'react';
import React, { useState } from 'react';
import {
  Button,
  Banner,
  Checkbox,
  Flashy,
  Form,
  FormGroup,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  IconButton,
  Input,
  Layout,
  Modal,
  ModalFooter,
  ModalSection,
  Paragraph,
  Icon,
  BadRequestError,
} from '@axiom/ui';
import type {
  User,
  CalendarEventType,
  Contact,
  AfcSubmission,
} from '@axiom/validation';

import type { InterviewFlowCandidate } from '../../types/interview-candidate.types';
import type { TalentInterviewConfirmModalType } from '../../schema/talent-interview-confirm-modal-schema';
import { TalentInterviewConfirmModalSchema } from '../../schema/talent-interview-confirm-modal-schema';
import { TalentInterviewModalHeader } from '../TalentInterviewModalHeader/TalentInterviewModalHeader';

export type InterviewConfirmModalType = {
  loggedInUser?: User;
  submission?: AfcSubmission;
  candidate: InterviewFlowCandidate;
  onClose: () => void;
  onConfirm: (
    arg: TalentInterviewConfirmModalType,
    scheduledEvent?: CalendarEventType
  ) => Promise<unknown>;
  scheduledEvent?: CalendarEventType;
  interviewDate?: string;
  primaryContactId?: Contact['id'];
  onBackHandler?: () => Promise<void>;
  availabilityNote?: string;
};

type ContactOptionType = { value: string; label: ReactNode };

export const TalentInterviewConfirmModal = ({
  loggedInUser,
  submission,
  candidate,
  onClose,
  onConfirm,
  scheduledEvent,
  interviewDate,
  primaryContactId,
  onBackHandler,
  availabilityNote,
}: InterviewConfirmModalType) => {
  const [adHocContactsCount, setAdHocContactsCount] = useState(0);
  const [isInterviewTimeUnavailable, setIsInterviewTimeUnavailable] =
    useState(false);
  const initValues: TalentInterviewConfirmModalType = {
    contacts: [],
    adHocContacts: [],
    isZoomNotPreferred: false,
    rateConfirmed: false,
  };

  let currentUserIsAContact: boolean | undefined;

  const contacts = submission?.contacts?.reduce<ContactOptionType[]>(
    (crnt, data: NonNullable<AfcSubmission['contacts']>[number]) => {
      const { fullName, email, id } = data;
      // Don't add the client user to the selection list if no linked user record exists
      if (!id) {
        return crnt;
      }

      let action: 'push' | 'unshift' = 'push';
      if (loggedInUser?.contactId === id) {
        action = 'unshift';
        currentUserIsAContact = true;
        initValues.contacts.push(id);
      } else if (
        primaryContactId &&
        id === primaryContactId &&
        !loggedInUser?.contactId
      ) {
        action = 'unshift';
        initValues.contacts.push(id);
      }

      crnt[action]({
        label: (
          <span className="max-lines-strict-2">
            {fullName} ({email})
          </span>
        ),
        value: id,
      });

      return crnt;
    },
    []
  );

  if (loggedInUser?.contactId && !currentUserIsAContact) {
    contacts?.unshift({
      label: (
        <span className="max-lines-strict-2">
          {loggedInUser.fullName} ({loggedInUser.email})
        </span>
      ),
      value: loggedInUser.contactId,
    });
    initValues.contacts.push(loggedInUser.contactId);
  }

  return (
    <Form
      name="TALENT_INTERVIEW_CONFIRM_MODAL_FORM"
      initialValues={initValues}
      onSubmit={async (changedData, { setFieldError }) => {
        try {
          const payload = { ...initValues, ...changedData };
          if (availabilityNote) {
            Object.assign(payload, { availabilityNote });
          }

          await onConfirm(payload, scheduledEvent);
        } catch (error) {
          if (error instanceof BadRequestError) {
            const errorObj = error.getResponse();

            if (errorObj?.text?.includes('domain')) {
              setFieldError(
                `errorMissMatchEmailDomain`,
                'For enhanced security, all attendees must have matching email domains from the same organization.'
              );

              return;
            }
            setIsInterviewTimeUnavailable(true);
          } else {
            throw error;
          }
        }
      }}
      schema={TalentInterviewConfirmModalSchema}
    >
      {({ fireSubmit, setValues, values }) => {
        return (
          <Modal name="TALENT_INTERVIEW_CONFIRM_MODAL" size="large">
            <TalentInterviewModalHeader
              candidate={candidate}
              name="INTERVIEW_CONFIRM_MODAL_HEADER"
              onClose={onClose}
            />
            <ModalSection>
              <Grid>
                {!!scheduledEvent && (
                  <GridRow gutterBottom="16px">
                    <GridColumn>
                      {!isInterviewTimeUnavailable && (
                        <Banner name="INTERVIEW_BANNER">
                          <Flashy bold color="controlSecondary">
                            Interview Date{' '}
                          </Flashy>
                          {interviewDate}
                        </Banner>
                      )}
                      {isInterviewTimeUnavailable ? (
                        <Banner
                          name="INTERVIEW_UNAVAILABLE_BANNER"
                          type="error"
                          impact="high"
                        >
                          <Layout position="middle" horizontalGutter="8px">
                            <Paragraph>
                              <Flashy color="textAlert">
                                <Icon name="alert-filled" />
                              </Flashy>
                            </Paragraph>
                            <Paragraph>
                              The selected interview time is no longer
                              available. Please try again.
                            </Paragraph>
                          </Layout>
                        </Banner>
                      ) : null}
                    </GridColumn>
                  </GridRow>
                )}

                <GridRow gutterBottom="16px">
                  <GridColumn>
                    <FormGroup
                      name="ALL_CONTACTS"
                      description="All contacts you select or add will be invited to join the interview."
                      label="Who else should we invite to the interview?"
                    >
                      {contacts?.map((contact: ContactOptionType) => (
                        <Gutter key={contact.value} bottom="8px">
                          <Checkbox
                            name="contacts"
                            displayValue={contact.label}
                            option={contact.value}
                          />
                        </Gutter>
                      ))}
                    </FormGroup>
                  </GridColumn>
                </GridRow>
                {Array.from({ length: adHocContactsCount })
                  .fill(null)
                  .map((_, i) => (
                    <React.Fragment key={`adHocContacts_${i.toString(10)}`}>
                      <GridRow
                        name="ADHOC_ROW"
                        stretched
                        columns={4}
                        gutterBottom="12px"
                      >
                        <GridColumn largeScreenWidth={2} smallScreenWidth={12}>
                          <Input
                            name={`adHocContacts.${i}.firstName`}
                            label="First Name"
                            placeholder="Entered Text"
                          />
                        </GridColumn>
                        <GridColumn largeScreenWidth={2} smallScreenWidth={12}>
                          <Input
                            name={`adHocContacts.${i}.lastName`}
                            label="Last Name"
                            placeholder="Entered Text"
                          />
                        </GridColumn>
                        <GridColumn largeScreenWidth={7} smallScreenWidth={10}>
                          <Input
                            name={`adHocContacts.${i}.email`}
                            label="Email"
                            placeholder="Entered Text"
                          />
                        </GridColumn>
                        <GridColumn largeScreenWidth={1} smallScreenWidth={2}>
                          <Gutter top="40px">
                            <IconButton
                              name="REMOVE_CONTACT"
                              icon="trash"
                              pattern="secondary"
                              variation="minimal"
                              onClick={async () => {
                                await setValues(prev => {
                                  const { adHocContacts } = prev;
                                  adHocContacts?.splice(i, 1);
                                  return { ...prev, adHocContacts };
                                }, false);

                                setAdHocContactsCount(prev => prev - 1);
                              }}
                            />
                          </Gutter>
                        </GridColumn>
                      </GridRow>
                      <GridRow gutterBottom="0">
                        <GridColumn width={12}>
                          {/* for displaying custom error */}
                          <FormGroup name="errorMissMatchEmailDomain" />
                        </GridColumn>
                      </GridRow>
                    </React.Fragment>
                  ))}

                <GridRow gutterBottom="12px">
                  <GridColumn width={12}>
                    {/* for displaying custom error */}
                    <FormGroup name="errorSelectAtLeastOne" />
                  </GridColumn>
                </GridRow>

                <GridRow>
                  <GridColumn>
                    <Button
                      pattern="secondary"
                      variation="minimal"
                      icon="plus"
                      iconPosition="right"
                      name="ADD_NEW_BUTTON"
                      onClick={async () => {
                        await setValues(
                          // @ts-expect-error Needs type fix with APCORE-2687
                          prev => ({
                            ...prev,
                            adHocContacts: [...(prev.adHocContacts ?? []), {}],
                          }),
                          false
                        );

                        setAdHocContactsCount(prev => prev + 1);
                      }}
                    >
                      Add Attendee
                    </Button>
                  </GridColumn>
                </GridRow>
              </Grid>
            </ModalSection>
            <ModalSection divider>
              <Checkbox
                option={values.isZoomNotPreferred}
                name="isZoomNotPreferred"
                displayValue="My company can't use Zoom. Let's discuss other options."
              />
            </ModalSection>
            <ModalSection divider>
              <FormGroup
                name="rateConfirmed"
                label="Confidential rate agreement"
                description="Axiom rates you receive are confidential Axiom information and may not be shared with legal talent. Our talent agree on their compensation when joining Axiom and are not able to modify or negotiate the rate shared with you. Please do not discuss rates directly with Axiom talent."
              >
                <Checkbox
                  name="rateConfirmed"
                  displayValue="I agree not to discuss rates directly with Axiom talent."
                />
              </FormGroup>
            </ModalSection>
            <ModalFooter stackableOn="mobile">
              {isInterviewTimeUnavailable ? (
                <Button name="BACK" variation="outline" onClick={onBackHandler}>
                  Back
                </Button>
              ) : (
                <>
                  <Button name="CANCEL" variation="minimal" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button name="CONFIRM" onClick={fireSubmit}>
                    {scheduledEvent ? 'Schedule' : 'Request'} Interview
                  </Button>
                </>
              )}
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
